import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { getStatusColor } from "../../../../utils/helper/tablehelper";
import { getOnboardingDashboard, updateOnboardingStatus } from "../../../../api/employeeApi";
import ClickableChip from "../../../../components/common/ClickableChip";
import PermissionGuard from "../../../../components/PermissionGuard";
import CustomNoResultsOverlay from "../employee-directory/components/CustomNoResultsOverlay";




const Documents = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "",
    department: "",
  });

  // Define status options for Document (similar to JoiningForm)
  const statusOptions = [
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'PENDING', label: 'Pending' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getOnboardingDashboard();
        if (response.success) {
          const formatted = response.data.map(emp => ({
            id: emp.id,
            empNo: emp.employeeId,
            name: emp.name,
            email: emp.email,
            role: emp.role,
            department: emp.department,
            employeeType: (emp.employeeType || "FULL_TIME").toUpperCase().replace("-", "_"),
            status: emp.onboardingSummary?.documents?.uploaded === emp.onboardingSummary?.documents?.required ? "COMPLETED" : (emp.onboardingSummary?.documents?.uploaded > 0 ? "IN_PROGRESS" : "PENDING"),
            documentsSubmitted: emp.onboardingSummary?.documents?.uploaded || 0,
            documentsRequired: emp.onboardingSummary?.documents?.required || 8,
            joiningDate: emp.joiningDate || new Date(),
          }));
          setEmployees(formatted);
        }
      } catch (error) {
        console.error("Error fetching documents data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Define columns for DataGrid
  const columns = [
    {
      field: "empNo",
      headerName: "EMPLOYEE NO.",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "name",
      headerName: "NAME",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "email",
      headerName: "EMAIL",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "role",
      headerName: "ROLE",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "department",
      headerName: "DEPARTMENT",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "employeeType",
      headerName: "EMPLOYEE TYPE",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      valueGetter: (value) => value.replace("_", " "),
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={true}  // Updates enabled for JoiningForm
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, "status", newStatus, params.value)}
        />
      ),
    },
    {
      field: "documents",
      headerName: "DOCUMENTS",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.documentsSubmitted}/{params.row.documentsRequired}
        </Typography>
      ),
    },
    {
      field: "joiningDate",
      headerName: "JOINING DATE",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      valueGetter: (value) => new Date(value).toLocaleDateString(),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      resizable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: () => (
        <IconButton size="small">
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    return employees.filter((employee) => {
      const matchesSearch =
        searchQuery === "" ||
        employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.empNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesEmployeeType =
        employeeTypeFilter === "ALL" ||
        employee.employeeType === employeeTypeFilter;

      const matchesStatus =
        statusFilter === "ALL" || employee.status === statusFilter;

      return matchesSearch && matchesEmployeeType && matchesStatus;
    });
  }, [searchQuery, employeeTypeFilter, statusFilter, employees]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      name: "",
      email: "",
      role: "",
      department: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    // Add your submit logic here
    handleCloseModal();
  };

  const handleStatusChange = (documentId, newStatus) => {
    setEmployees(prevEmployees =>
      prevEmployees.map(emp =>
        emp.id === documentId
          ? { ...emp, status: newStatus }
          : emp
      )
    );

    // Add your API call here when ready
    // await updateDocumentStatus(documentId, newStatus);
  };

  const handleBulkUpload = (event) => {
  };

  return (
    <Box sx={{ mt: 0, backgroundColor: '#fff' }}>
      <Box
        sx={{
          backgroundColor: '#F5F5F5',
          padding: '12px 12px',
          marginBottom: 2,
          borderRadius: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          width: "100%"
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#030007',
              marginBottom: 0.5,
              fontSize: '1.1rem',
            }}
          >
            Documents
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4F4937',
              fontSize: '0.9rem',
            }}
          >
            All Documenst collected real-Time data
          </Typography>
        </Box>

      </Box>
      <Grid container spacing={2}>
        {/* Filters Section */}
        <Grid item size={{ xs: 12 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>


            <Grid item size={{ xs: 12, sm: 6, md: 3 }}>

              <TextField
                placeholder="Search by Employee ID, Name, Email..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Employee Type</InputLabel>
                <Select
                  value={employeeTypeFilter}
                  label="Employee Type"
                  onChange={(e) => setEmployeeTypeFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Types</MenuItem>
                  <MenuItem value="FULL_TIME">Full Time</MenuItem>
                  <MenuItem value="PART_TIME">Part Time</MenuItem>
                  <MenuItem value="CONTRACT">Contract</MenuItem>
                  <MenuItem value="INTERN">Intern</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </Grid>

        {/* DataGrid Section */}
        <Grid item size={{ xs: 12 }}>
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <DataGrid
              rows={filteredData}
              columns={columns}
              loading={loading}
              checkboxSelection={false}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: CustomNoResultsOverlay,
              }}
              sx={{
                height: 520,
                width: "100%",
                backgroundColor: "white",
                border: 1,
                borderColor: "#e0e0e0",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#eaf2ff",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  alignItems: "baseline !important",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                },
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 25, 50]}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Initiate Onboarding Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Initiate Onboarding
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Employee Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item size={{ xs: 12 }}>
              <Box
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <input
                  accept=".xlsx,.xls,.csv"
                  style={{ display: "none" }}
                  id="bulk-upload-file"
                  type="file"
                  onChange={handleBulkUpload}
                />
                <label htmlFor="bulk-upload-file">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    Bulk Upload (Excel)
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Upload Excel file with employee details
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#7522D1",
              "&:hover": { backgroundColor: "#5e1ba8" },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documents;
